import React, { useState } from 'react';

import { ReactComponent as DotCircleIcon } from '../../../../assets/icons/custom-dashboard/dount-chart.svg';
import { ReactComponent as ActiveSharingIcon } from '../../../../assets/icons/custom-dashboard/line-chart.svg';
import { ReactComponent as PieChartIcon } from '../../../../assets/icons/custom-dashboard/pie-chart.svg';
import { ReactComponent as GraphIcon } from '../../../../assets/icons/custom-dashboard/vertical-bar.svg';
import { ReactComponent as DownArrowIcon } from '../../../../assets/icons/down-arrow.svg';
import { ReactComponent as RightArrowIcon } from '../../../../assets/icons/right-arrow.svg';

interface ChartDataEntry {
  chart_type: string;
  chartdata: any;
}

interface ChartDetail {
  chart_heading: string;
  data: ChartDataEntry[];
}

// interface KPIChild {
//     kpi_title: string;
//     value: string;
//     change: string;
//     change_type: string;
//     period: string;
//     icon: string;
//     data: ChartDataEntry;
// }

interface SubheadingItem {
  subheading?: string;
  chartdetails?: ChartDetail[];
  // KPI fields as optional to allow the array to be mixed or to satisfy the union
  kpi_title?: string;
  value?: string;
  data?: ChartDataEntry;
}

interface DashboardModule {
  id: string;
  mainheading: string;
  children?: (SubheadingItem & any)[];
  type?: string;
  description?: string;
  data?: any;
}

interface SidebarModuleProperties {
  module: DashboardModule;
  selectedCharts: string[];
  onToggleChart: (chartId: string, chartData: any) => void;
}

/**
 * Handles keyboard events for interactive elements.
 * @param {any} event - The keyboard event.
 * @param {any} action - The action to perform.
 */
const handleKeyDown = (event: React.KeyboardEvent, action: () => void) => {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    action();
  }
};
// Helper to determine if an item should use a square checkbox
/**
 * Name
 * @param {any} label - The label
 * @param {any} type - The type
 * @returns {any} The result
 */
const isSquare = (label: string, type?: string) => {
  const lowerLabel = label.toLowerCase();
  return (
    type === 'map' ||
    lowerLabel.includes('live alerts') ||
    lowerLabel.includes('station performance') ||
    lowerLabel.includes('heatmap')
  );
};

/**
 * Helper to get an icon based on chart type.
 * @param {any} type - The type
 */
const getChartIcon = (type: string) => {
  const lowerType = type.toLowerCase();
  if (lowerType.includes('pie')) return <PieChartIcon />;
  if (lowerType.includes('donut')) return <DotCircleIcon />;
  if (lowerType.includes('line')) return <ActiveSharingIcon />;
  if (lowerType.includes('heatmap')) return <ActiveSharingIcon />;
  if (lowerType.includes('bar')) {
    if (lowerType.includes('horizontal'))
      return <GraphIcon style={{ transform: 'rotate(90deg)' }} />;
    return <GraphIcon />;
  }
  return <GraphIcon />;
};

/**
 * SidebarModule renders a main module heading and its expandable subheadings/charts.
 * @param {any} props - The component properties.
 * @param {any} props.module - The module prop
 * @param {any} props.selectedCharts - The selectedCharts prop
 * @param {any} props.onToggleChart - The onToggleChart prop
 * @returns {any} The rendered component.
 */
function SidebarModule({ module, selectedCharts, onToggleChart }: SidebarModuleProperties) {
  const [isModuleExpanded, setIsModuleExpanded] = useState(true);
  const [expandedSubheadings, setExpandedSubheadings] = useState<string[]>([]);

  /**
   * Toggles the expansion of a subheading.
   * @param {string} subheading - The subheading text.
   */
  const toggleSubheading = (subheading: string) => {
    setExpandedSubheadings((previous) =>
      previous.includes(subheading)
        ? previous.filter((s) => s !== subheading)
        : [...previous, subheading],
    );
  };

  // If it's a special type like 'map'
  if (module.type === 'map') {
    const isSelected = selectedCharts.includes('station-map');
    return (
      <div className="sidebar-module">
        <div className="sidebar-item-container">
          <div
            className="sidebar-item"
            onClick={() => onToggleChart('station-map', { ...module, heading: module.mainheading })}
            onKeyDown={(event) =>
              handleKeyDown(event, () =>
                onToggleChart('station-map', { ...module, heading: module.mainheading }),
              )
            }
            role="button"
            tabIndex={0}>
            <div className={`square-checkbox ${isSelected ? 'active' : ''}`} />
            <span className="item-label">{module.mainheading}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="sidebar-module">
      <div
        className="category-title"
        onClick={() => setIsModuleExpanded(!isModuleExpanded)}
        onKeyDown={(event) => handleKeyDown(event, () => setIsModuleExpanded(!isModuleExpanded))}
        role="button"
        tabIndex={0}
        style={{ cursor: 'pointer' }}>
        {module.mainheading}
        {isModuleExpanded ? (
          <DownArrowIcon width={10} height={10} />
        ) : (
          <RightArrowIcon width={10} height={10} />
        )}
      </div>

      {isModuleExpanded && module.children && (
        <div className="category-items">
          {module.children.map((child, index) => {
            const isSubExpanded = child.subheading
              ? expandedSubheadings.includes(child.subheading)
              : false;
            const isMainActive =
              child.chartdetails?.some((d: ChartDetail) =>
                d.data.some((c: ChartDataEntry) =>
                  selectedCharts.includes(`${d.chart_heading}-${c.chart_type}`),
                ),
              ) ||
              (child.kpi_title && selectedCharts.includes(`${child.kpi_title}-sparkline`));

            // Render KPI item differently or just handle the label
            const label = child.subheading || child.kpi_title || '';
            const isKPI = !!child.kpi_title;
            const useSquare = isSquare(label);

            if (isKPI) {
              const chartId = `${child.kpi_title}-sparkline`;
              const isSelected = selectedCharts.includes(chartId);
              return (
                <div key={index} className="sidebar-item-container">
                  <div
                    className="sidebar-item"
                    onClick={() =>
                      onToggleChart(chartId, { ...child.data, heading: child.kpi_title })
                    }
                    onKeyDown={(event) =>
                      handleKeyDown(event, () =>
                        onToggleChart(chartId, { ...child.data, heading: child.kpi_title }),
                      )
                    }
                    role="button"
                    tabIndex={0}>
                    <div
                      className={`${useSquare ? 'square-checkbox' : 'square-checkbox'} ${isSelected ? 'active' : ''}`}
                    />
                    <span className="item-label">{label}</span>
                  </div>
                </div>
              );
            }

            return (
              <div key={index} className="sidebar-item-container">
                <div
                  className={`sidebar-item ${isSubExpanded ? 'expanded' : ''}`}
                  onClick={() => {
                    const isSingleton =
                      child.chartdetails?.length === 1 && child.chartdetails[0].data.length === 1;

                    if (isSingleton) {
                      const detail = child.chartdetails![0];
                      const chart = detail.data[0];
                      const chartId = `${detail.chart_heading}-${chart.chart_type}`;
                      onToggleChart(chartId, { ...chart, heading: detail.chart_heading });
                    } else if (child.subheading) {
                      toggleSubheading(child.subheading);
                    }
                  }}
                  onKeyDown={(event) =>
                    handleKeyDown(event, () => {
                      const isSingleton =
                        child.chartdetails?.length === 1 && child.chartdetails[0].data.length === 1;
                      if (isSingleton) {
                        const detail = child.chartdetails![0];
                        const chart = detail.data[0];
                        onToggleChart(`${detail.chart_heading}-${chart.chart_type}`, {
                          ...chart,
                          heading: detail.chart_heading,
                        });
                      } else if (child.subheading) {
                        toggleSubheading(child.subheading);
                      }
                    })
                  }
                  role="button"
                  tabIndex={0}>
                  <div
                    className={`${useSquare ? 'square-checkbox' : 'square-checkbox'} ${isMainActive ? 'active' : ''}`}
                  />
                  <span className="item-label">{label}</span>
                  {child.chartdetails &&
                    !(
                      child.chartdetails?.length === 1 && child.chartdetails[0].data.length === 1
                    ) &&
                    (isSubExpanded ? (
                      <DownArrowIcon className="arrow-down" />
                    ) : (
                      <RightArrowIcon className="arrow-right" />
                    ))}
                </div>

                {!(child.chartdetails?.length === 1 && child.chartdetails[0].data.length === 1) &&
                  isSubExpanded &&
                  child.chartdetails && (
                    <div className="sub-item-details">
                      {child.chartdetails.map((detail: ChartDetail, dIndex: number) => (
                        <div key={dIndex} className="chart-group">
                          <div className="chart-selection-grid">
                            {detail.data.map((chart: ChartDataEntry, cIndex: number) => {
                              const chartId = `${detail.chart_heading}-${chart.chart_type}`;
                              const isSelected = selectedCharts.includes(chartId);
                              return (
                                <div
                                  key={cIndex}
                                  className={`chart-tile ${isSelected ? 'active' : ''}`}
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    onToggleChart(chartId, {
                                      ...chart,
                                      heading: detail.chart_heading,
                                    });
                                  }}
                                  onKeyDown={(event) =>
                                    handleKeyDown(event, () =>
                                      onToggleChart(chartId, {
                                        ...chart,
                                        heading: detail.chart_heading,
                                      }),
                                    )
                                  }
                                  role="button"
                                  tabIndex={0}>
                                  <div className={`tile-checkbox ${isSelected ? 'active' : ''}`} />
                                  <div className="icon-box">{getChartIcon(chart.chart_type)}</div>
                                  <span className="chart-label">{chart.chart_type}</span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default SidebarModule;
